import { useEffect, useRef, useCallback } from 'react';
import { useIsPremium } from '../stores/premiumStore';
import { useConsentStore } from '../stores/consentStore';

declare global {
  interface Window {
    adsbygoogle: Record<string, unknown>[];
  }
}

type AdFormat = 'in-article' | 'in-feed' | 'auto';

interface AdUnitProps {
  /** AdSense ad slot ID */
  slot: string;
  /** Ad format — defaults to 'in-article' for minimal blending */
  format?: AdFormat;
  /** Optional extra className for the wrapper */
  className?: string;
}

const ADS_ENABLED = import.meta.env.VITE_ADS_ENABLED === 'true';
const ADSENSE_CLIENT_ID = (typeof window !== 'undefined' && (window as any).__ENV__?.VITE_ADSENSE_CLIENT_ID) || import.meta.env.VITE_ADSENSE_CLIENT_ID || 'ca-pub-5654331809788824';
const ADSENSE_SCRIPT_URL = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT_ID}`;

/** Tracks whether the AdSense script tag has already been injected into the page. */
let adsenseScriptInjected = false;

/**
 * Dynamically loads the Google AdSense script into the document head.
 * Only called when the user has explicitly consented to advertising cookies.
 * This ensures ePrivacy Directive compliance — no ad scripts load without consent.
 */
function loadAdsenseScript(): void {
  if (adsenseScriptInjected) return;
  if (document.querySelector(`script[src*="adsbygoogle"]`)) {
    adsenseScriptInjected = true;
    return;
  }
  const script = document.createElement('script');
  script.src = ADSENSE_SCRIPT_URL;
  script.async = true;
  script.crossOrigin = 'anonymous';
  document.head.appendChild(script);
  adsenseScriptInjected = true;
}

/**
 * Minimal, native-looking AdSense ad unit.
 * - Disabled entirely via VITE_ADS_ENABLED=false
 * - Hidden for premium users
 * - Blocked until user consents to advertising cookies (ePrivacy)
 * - Blends with the site's card design system
 * - No "Ad" label to keep it discreet (AdSense handles its own labeling)
 */
export default function AdUnit({ slot, format = 'in-article', className = '' }: AdUnitProps) {
  const isPremium = useIsPremium();
  const advertisingConsent = useConsentStore((s) => s.advertising);
  const adRef = useRef<HTMLDivElement>(null);
  const pushed = useRef(false);

  const canShowAds = ADS_ENABLED && !isPremium && advertisingConsent;

  // Reset pushed flag when ads become disabled so re-consent can push again
  useEffect(() => {
    if (!canShowAds) {
      pushed.current = false;
    }
  }, [canShowAds]);

  // Load the AdSense script only once advertising consent is given
  useEffect(() => {
    if (!canShowAds) return;
    loadAdsenseScript();
  }, [canShowAds]);

  const pushAd = useCallback(() => {
    if (!canShowAds || pushed.current) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      pushed.current = true;
    } catch {
      // AdSense not loaded (dev, adblock, etc.) — fail silently
    }
  }, [canShowAds]);

  useEffect(() => {
    pushAd();
  }, [pushAd]);

  if (!canShowAds) return null;

  return (
    <div
      ref={adRef}
      className={`w-full overflow-hidden rounded-2xl border border-slate-200/70 bg-slate-50/60 dark:border-white/10 dark:bg-white/5 ${className}`}
    >
      <ins
        className="adsbygoogle"
        style={{ display: 'block', textAlign: 'center' }}
        data-ad-client={ADSENSE_CLIENT_ID}
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive="true"
      />
    </div>
  );
}

/**
 * Inline ad variant — sits between content sections.
 * Uses the same card aesthetic as the rest of the dashboard.
 */
export function InlineAd({ slot, className = '' }: { slot: string; className?: string }) {
  return <AdUnit slot={slot} format="in-article" className={`my-6 ${className}`} />;
}

/**
 * Feed ad variant — sits between list/grid items.
 */
export function FeedAd({ slot, className = '' }: { slot: string; className?: string }) {
  return <AdUnit slot={slot} format="in-feed" className={`my-4 ${className}`} />;
}
